import { useListState } from '../../navigation/useListState'
import React, { useEffect, useMemo, useState } from 'react'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../navigation/navigation'
import api from '../../services/api'
import { Button, Input, Select, TagBadge } from '../../components/ui'
import Icon from '../../components/Icon'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { formatStagePlannedDate, isObjectStagePlannedDateOverdue } from './budgetStageListUtils'
import './Company.css'

function normalize(value) {
  return String(value || '').trim().toLowerCase()
}

function uniqueOptions(items, getValue) {
  const seen = new Set()
  return items
    .map(getValue)
    .filter(Boolean)
    .filter((value) => {
      const key = normalize(value)
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((a, b) => String(a).localeCompare(String(b), 'ru'))
}

function getStageListName(stage) {
  const stageName = stage?.name || stage?.type || ''
  return [stageName, stage?.object_name].filter(Boolean).join(' ') || '-'
}

function Budgets() {
  const { filters, setFilters } = useListState({
    filters: {
      object: '',
      type: '',
      search: '',
    },
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canViewSummaryActs = user?.permissions?.['stages.view']
  usePageHeader({ hideTopHeader: true }, [])
  const [stages, setStages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function fetchStages() {
      try {
        setLoading(true)
        setError(null)
        const response = await api.get('/company/object-stages')
        if (!cancelled) setStages(response.data?.stages || [])
      } catch (err) {
        if (!cancelled) {
          setStages([])
          setError('Не удалось загрузить стадии')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchStages()
    return () => {
      cancelled = true
    }
  }, [])

  const objectOptions = useMemo(() => uniqueOptions(stages, (stage) => stage.object_name), [stages])
  const typeOptions = useMemo(() => uniqueOptions(stages, (stage) => stage.type), [stages])

  const filteredStages = useMemo(() => {
    const search = normalize(filters.search)
    return stages.filter((stage) => {
      if (filters.object && stage.object_name !== filters.object) return false
      if (filters.type && stage.type !== filters.type) return false
      if (search) {
        const haystack = normalize([
          getStageListName(stage),
          stage.name,
          stage.object_name,
          stage.type,
        ].filter(Boolean).join(' '))
        if (!haystack.includes(search)) return false
      }
      return true
    })
  }, [stages, filters])

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="company-page">
      <div className="page-header budgets-page-header">
        <div>
          <h1>Бюджеты</h1>
        </div>
        <div className="page-header-actions">
          {canViewSummaryActs && (
            <Button variant="secondary" onClick={() => navigate('/company/stages/summary-report')}>
              Сводный отчёт
            </Button>
          )}
          <Button variant="primary" onClick={() => navigate('/company/budgets/new')}>
            <Icon name="plus" size={12} /> Создать стадию
          </Button>
        </div>
      </div>
      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label>Объект</label>
            <Select
              value={filters.object}
              onChange={(event) => handleFilterChange('object', event.target.value)}
              aria-label="Объект"
            >
              <option value="">Все объекты</option>
              {objectOptions.map((objectName) => (
                <option key={objectName} value={objectName}>{objectName}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Тип</label>
            <Select
              value={filters.type}
              onChange={(event) => handleFilterChange('type', event.target.value)}
              aria-label="Тип"
            >
              <option value="">Все типы</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group filter-group-search">
            <label>Поиск</label>
            <Input
              type="text"
              placeholder="Поиск по стадии, объекту или типу"
              value={filters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              aria-label="Поиск по стадиям"
            />
          </div>
        </div>
      </div>

      {error && <div className="message message-error" role="alert">{error}</div>}

      <div className="table-section">
        {filteredStages.length === 0 ? (
          <div className="no-data">Стадии не найдены</div>
        ) : isMobile ? (
          <div className="list-cards">
            {filteredStages.map((stage) => (
              <div
                key={stage.id}
                className="list-card ui-card ui-card-hover"
              >
                <div className="list-card-header">
                  <Link className="list-card-title" to={`/company/budgets/${encodeURIComponent(stage.public_id || stage.id)}`}>
                    {getStageListName(stage)}
                  </Link>
                </div>
                <div className="list-card-body">
                  <div className="list-card-row">
                    <span className="list-card-label">Объект:</span>
                    <span className="list-card-value">{stage.object_name || '-'}</span>
                  </div>
                  <div className="list-card-row">
                    <span className="list-card-label">Плановая дата:</span>
                    <span className={`list-card-value${isObjectStagePlannedDateOverdue(stage) ? ' budgets-planned-date-overdue' : ''}`}>
                      {formatStagePlannedDate(stage.planned_date)}
                    </span>
                  </div>
                  <div className="list-card-row">
                    <span className="list-card-label">Тип:</span>
                    <span className="list-card-value">
                      {stage.type ? <TagBadge>{stage.type}</TagBadge> : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="company-table-frame">
            <div className="table-responsive">
              <table className="ui-table data-table">
                <thead>
                  <tr>
                    <th>Объект</th>
                    <th>Название стадии</th>
                    <th>Тип</th>
                    <th>Плановая дата</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStages.map((stage) => {
                    const plannedOverdue = isObjectStagePlannedDateOverdue(stage)
                    return (
                    <tr
                      key={stage.id}
                      className="clickable-row"
                      onClick={() => navigate(`/company/budgets/${encodeURIComponent(stage.public_id || stage.id)}`)}
                    >
                      <td>{stage.object_name || '-'}</td>
                      <td>
                        <Link className="list-primary-name" to={`/company/budgets/${encodeURIComponent(stage.public_id || stage.id)}`}>
                          {getStageListName(stage)}
                        </Link>
                      </td>
                      <td>{stage.type ? <TagBadge>{stage.type}</TagBadge> : '-'}</td>
                      <td className={plannedOverdue ? 'budgets-planned-date-overdue' : undefined}>
                        {formatStagePlannedDate(stage.planned_date)}
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Budgets
